import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { LoginInitiateResponse } from "@its-engineering-and-development/jwt-auth-client";
import {
  clearAuthSession,
  ensureFreshAccessToken,
  exchangeOneTimeCode,
  getCurrentAuthUser,
  getStoredAccessToken,
  initiateLogin,
  verifyMfaCode,
  type AuthUser,
} from "@app/lib/auth/auth-session";
import { AUTH_STATUS, type AuthStatus } from "@app/lib/auth-constants";
import { authTokenStore } from "@app/lib/auth/token-store";
import { onSessionExpired } from "@app/lib/auth/auth-events";

type JwtAuthContextValue = {
  status: AuthStatus;
  isAuthenticated: boolean;
  user: AuthUser | null;
  login: (email: string) => Promise<LoginInitiateResponse>;
  verifyMfa: (mfaTicket: string, code: string) => Promise<void>;
  logout: () => void;
  exchangeCode: (code: string) => Promise<void>;
  ensureActiveSession: () => Promise<string | null>;
};

const JwtAuthContext = createContext<JwtAuthContextValue | null>(null);

function unauthenticatedState() {
  return {
    status: AUTH_STATUS.UNAUTHENTICATED,
    user: null,
  };
}

function authenticatedState(user: AuthUser | null) {
  if (!user) {
    return unauthenticatedState();
  }

  return {
    status: AUTH_STATUS.AUTHENTICATED,
    user,
  };
}

export function JwtAuthProvider({ children }: PropsWithChildren) {
  const [authState, setAuthState] = useState<{
    status: AuthStatus;
    user: AuthUser | null;
  }>({
    status: AUTH_STATUS.LOADING,
    user: null,
  });

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      const token = getStoredAccessToken();
      const refreshToken = authTokenStore.getRefreshToken();

      // Only short-circuit to "unauthenticated" when we truly have no session
      // material at all. The access token can be missing (or temporarily blanked
      // during an in-flight refresh) while a valid refresh token is still
      // present — in that case we must let `ensureFreshAccessToken()` attempt a
      // refresh instead of bouncing the user to /login.
      if (!token && !refreshToken) {
        if (isMounted) {
          setAuthState(unauthenticatedState());
        }
        return;
      }

      try {
        const freshToken = await ensureFreshAccessToken();

        if (!isMounted) {
          return;
        }

        if (!freshToken) {
          setAuthState(unauthenticatedState());
          return;
        }

        setAuthState(authenticatedState(getCurrentAuthUser()));
      } catch {
        clearAuthSession();

        if (isMounted) {
          setAuthState(unauthenticatedState());
        }
      }
    };

    void initialize();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return onSessionExpired(() => {
      clearAuthSession();
      setAuthState(unauthenticatedState());
    });
  }, []);

  const ensureActiveSession = useCallback(async (): Promise<string | null> => {
    try {
      const token = await ensureFreshAccessToken();
      setAuthState(authenticatedState(getCurrentAuthUser()));
      return token;
    } catch (error) {
      setAuthState(unauthenticatedState());
      throw error;
    }
  }, []);

  const login = useCallback(
    async (email: string): Promise<LoginInitiateResponse> => {
      return initiateLogin(email);
    },
    [],
  );

  const verifyMfa = useCallback(
    async (mfaTicket: string, code: string): Promise<void> => {
      await verifyMfaCode(mfaTicket, code);
      setAuthState(authenticatedState(getCurrentAuthUser()));
    },
    [],
  );

  const exchangeCode = useCallback(async (code: string) => {
    await exchangeOneTimeCode(code);
    setAuthState(authenticatedState(getCurrentAuthUser()));
  }, []);

  const logout = useCallback(() => {
    clearAuthSession();
    setAuthState(unauthenticatedState());
  }, []);

  const contextValue = useMemo<JwtAuthContextValue>(
    () => ({
      status: authState.status,
      isAuthenticated: authState.status === AUTH_STATUS.AUTHENTICATED,
      user: authState.user,
      login,
      verifyMfa,
      logout,
      exchangeCode,
      ensureActiveSession,
    }),
    [authState, ensureActiveSession, exchangeCode, login, logout, verifyMfa],
  );

  return (
    <JwtAuthContext.Provider value={contextValue}>
      {children}
    </JwtAuthContext.Provider>
  );
}

export function useJwtAuth() {
  const context = useContext(JwtAuthContext);

  if (!context) {
    throw new Error("useJwtAuth must be used within JwtAuthProvider.");
  }

  return context;
}
